import React from 'react'
import { OctagonAlert, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import type { RoomsSetupRoomType } from '@/types/shared'

/**
 * One failed Rooms Setup save, explained in place.
 *
 * The save is atomic — the whole batch applies together or not at all — so the FIRST thing this
 * panel says is that nothing was saved and the operator's staged edits are still here. Below
 * that, one row per room the backend named: the sentence is composed server-side
 * (UpdateRoomLinksMutation) in the page's own vocabulary ("follows", "prices independently"),
 * and this panel only anchors it to the room it names, by the name the page lists it under, then
 * the sentence. No raw validation strings, and never a toast: a batch failure the
 * operator has to fix room-by-room cannot live in a surface that vanishes.
 */

export interface RoomsSetupSaveErrorItem {
  roomTypeId: number | null
  message: string
}

/**
 * The `errors.roomsSetup` payload from a failed save, defensively parsed — the errors prop is
 * shared machinery and this page must never crash on an unexpected shape.
 */
export function parseRoomsSetupErrors(value: unknown): RoomsSetupSaveErrorItem[] | null {
  if (!Array.isArray(value)) return null
  const items: RoomsSetupSaveErrorItem[] = []
  for (const entry of value) {
    if (entry == null || typeof entry !== 'object') continue
    const message = (entry as { message?: unknown }).message
    if (typeof message !== 'string' || message.trim() === '') continue
    const rawId = (entry as { roomTypeId?: unknown }).roomTypeId
    items.push({
      roomTypeId: typeof rawId === 'number' && Number.isFinite(rawId) ? rawId : null,
      message,
    })
  }
  return items.length > 0 ? items : null
}

interface RoomsSetupSaveErrorProps {
  items: RoomsSetupSaveErrorItem[]
  roomTypes: RoomsSetupRoomType[]
  onDismiss: () => void
}

export default function RoomsSetupSaveError({ items, roomTypes, onDismiss }: RoomsSetupSaveErrorProps) {
  const roomFor = (id: number | null) =>
    id == null ? undefined : roomTypes.find((rt) => rt.id === id)

  return (
    <div
      role="alert"
      data-testid="rooms-setup-save-error"
      className="animate-slide-in overflow-hidden rounded-xl border border-rose-200 bg-rose-50/60 shadow-sm"
    >
      <div className="flex items-start gap-3 px-5 py-4">
        <OctagonAlert className="mt-0.5 h-4 w-4 shrink-0 text-rose-600" aria-hidden />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-rose-900">
            These changes can&apos;t be saved together
          </p>
          <p className="mt-0.5 text-xs leading-relaxed text-rose-800/80">
            Nothing was saved — the changes apply together or not at all, and your staged edits
            are still here. Adjust the rooms below and save again.
          </p>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-8 w-8 shrink-0 p-0 text-rose-500 hover:bg-rose-100 hover:text-rose-600"
          onClick={onDismiss}
          aria-label="Dismiss save error"
        >
          <X className="h-3.5 w-3.5" />
        </Button>
      </div>

      <ul className="space-y-3 border-t border-rose-200/60 px-5 py-4">
        {items.map((item, idx) => {
          const room = roomFor(item.roomTypeId)
          return (
            <li key={`${item.roomTypeId ?? 'general'}-${idx}`} className="flex gap-2.5">
              <span
                aria-hidden
                className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-rose-400"
              />
              <div className="min-w-0">
                {room && (
                  <span className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-semibold text-foreground">{room.name}</span>
                  </span>
                )}
                <p className="mt-0.5 text-sm leading-relaxed text-rose-900/90">{item.message}</p>
              </div>
            </li>
          )
        })}
      </ul>
    </div>
  )
}
